// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// La session vue du navigateur (chantier ⑯, 30/09/2026).
//
// ⛔ LA PAGE N'A PLUS JAMAIS LE JETON EN MAIN. Il vit dans un cookie `HttpOnly`
// (`ns_acces`) que le serveur pose et relit ; aucun script — ni le nôtre, ni
// celui d'une faille XSS — ne peut le lire. Ne JAMAIS le remettre dans
// `localStorage`, ni dans un en-tête `Authorization`.
//
// Ce module enveloppe `window.fetch` UNE FOIS (`installerFetchDeSession`, dans
// index.js), pour TOUS les appels vers l'API — il y en a près de 150, et en
// oublier un seul casserait la production :
//   1. `credentials: 'include'` : en développement, le front (port 3000) et
//      l'API (port 5000) sont deux origines ; sans cela aucun cookie ne part ;
//   2. `X-CSRF-Token` sur les requêtes MODIFIANTES : un cookie part tout seul,
//      le serveur exige donc la preuve que la requête vient de notre page
//      (double soumission : il compare l'en-tête au cookie `ns_csrf`, que seule
//      une page de notre site peut lire) ;
//   3. un 401 ⇒ UN renouvellement (`/api/auth/refresh`), puis la requête est
//      rejouée une fois. Le jeton d'accès ne vit que 15 min : sans cela,
//      l'utilisateur serait déconnecté tous les quarts d'heure. Si le
//      renouvellement échoue (plafond de 12 h, déconnexion ailleurs, vol
//      détecté), l'événement `normascan:session-perdue` prévient AuthContext.

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const METHODES_MODIFIANTES = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// Routes qui ne doivent JAMAIS déclencher de renouvellement sur un 401 : leur
// 401 veut dire « mauvais identifiants » ou « session finie », pas « jeton expiré ».
const SANS_RENOUVELLEMENT = [
  '/api/login', '/api/register', '/api/logout', '/api/auth/refresh',
  '/api/auth/mfa/verify', '/api/auth/mfa/setup',
];

export const EVENEMENT_SESSION_PERDUE = 'normascan:session-perdue';

let fetchNatif = null;

export function lireCsrf() {
  const trouve = document.cookie.match(/(?:^|;\s*)ns_csrf=([^;]*)/);
  return trouve ? decodeURIComponent(trouve[1]) : '';
}

const cheminApi = (url) => {
  if (url.startsWith(`${API}/api/`)) return url.slice(API.length);
  if (url.startsWith('/api/')) return url;
  return null;
};

// Un seul renouvellement à la fois : dix requêtes qui expirent ensemble ne
// doivent pas en lancer dix (le serveur tolère deux onglets, pas dix appels).
let renouvellementEnCours = null;

export function renouvelerSession() {
  // Pas de jeton anti-CSRF ⇒ pas de session ouverte (visiteur anonyme, ou
  // session déjà close) : rien à renouveler, inutile de solliciter le serveur.
  if (!lireCsrf()) return Promise.resolve(false);
  if (!renouvellementEnCours) {
    renouvellementEnCours = (fetchNatif || window.fetch)(`${API}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-CSRF-Token': lireCsrf() },
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => { renouvellementEnCours = null; });
  }
  return renouvellementEnCours;
}

const preparer = (init, methode) => {
  const entetes = new Headers(init.headers || {});
  if (METHODES_MODIFIANTES.has(methode)) {
    const csrf = lireCsrf();
    if (csrf) entetes.set('X-CSRF-Token', csrf);
  }
  return { ...init, headers: entetes, credentials: 'include' };
};

export function installerFetchDeSession() {
  if (fetchNatif) return;               // idempotent (rechargement à chaud)
  fetchNatif = window.fetch.bind(window);

  window.fetch = async (ressource, init = {}) => {
    const url = typeof ressource === 'string' ? ressource
      : (ressource instanceof URL ? ressource.href : null);
    const chemin = url ? cheminApi(url) : null;
    if (!chemin) return fetchNatif(ressource, init);   // Stripe, fichiers statiques…

    const methode = (init.method || 'GET').toUpperCase();
    const reponse = await fetchNatif(ressource, preparer(init, methode));

    if (reponse.status !== 401
        || SANS_RENOUVELLEMENT.some((c) => chemin.startsWith(c))
        || new Headers(init.headers || {}).has('Authorization')) {   // jeton MFA provisoire
      return reponse;
    }

    if (await renouvelerSession()) {
      // Rejouée avec le NOUVEAU jeton anti-CSRF éventuel : on relit le cookie.
      return fetchNatif(ressource, preparer(init, methode));
    }
    window.dispatchEvent(new CustomEvent(EVENEMENT_SESSION_PERDUE));
    return reponse;
  };
}
